/**
 * Cliente HTTP genérico para llamar al backend.
 * Inyecta `Authorization: Bearer`, `Accept-Language` y opcionalmente
 * `X-Active-Tenant`.
 *
 * i18n: el header `Accept-Language` se toma del idioma activo en i18next
 * (Doc 19 §3.2). El backend lo usa para localizar `error.message`. El
 * `error.code` siempre viene en inglés (contrato).
 */

import i18n from '../i18n/config'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000'

export interface ApiFetchOptions {
  accessToken: string
  /**
   * UUID del tenant activo para este request. Cuando se setea, el backend
   * lo respeta (Docs/12 §"Selector de tenant activo"). En endpoints que NO
   * son tenant-scoped (ej. /me) puede omitirse — el backend igual procesa.
   */
  activeTenantId?: string | undefined
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
}

/**
 * Error estructurado del backend — shape compatible con Docs/13-API.md
 * §"Error". `code` es el contrato estable (siempre inglés); `message`
 * viene localizado por `Accept-Language`.
 */
export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
  request_id?: string
  trace_id?: string
}

export class ApiError extends Error {
  /** Code estable del backend ('ARTIFACT_NOT_FOUND', etc.). Cuando es
   * 'UNKNOWN', el body no respetaba el contrato y caemos al texto crudo. */
  public readonly code: string
  /** Body parseado del backend si seguía el shape estándar. */
  public readonly body: ApiErrorBody | null
  /** Texto crudo del response (fallback si el JSON no parsea). */
  public readonly rawBody: string

  constructor(public readonly status: number, rawBody: string) {
    let body: ApiErrorBody | null = null
    try {
      const parsed = JSON.parse(rawBody) as { error?: ApiErrorBody }
      if (parsed && typeof parsed === 'object' && parsed.error) {
        body = parsed.error
      }
    } catch {
      body = null
    }
    super(body?.message ?? `HTTP ${status}: ${rawBody}`)
    this.name = 'ApiError'
    this.code = body?.code ?? 'UNKNOWN'
    this.body = body
    this.rawBody = rawBody
  }

  /** Error con el shape estándar sin pasar por un response HTTP (p. ej. el
   * evento `error` de un stream SSE). */
  static fromBody(status: number, body: ApiErrorBody): ApiError {
    return new ApiError(status, JSON.stringify({ error: body }))
  }
}

/**
 * El backend negó el recurso por el tier del usuario (`403 FORBIDDEN_TIER`,
 * `app/core/permissions.py`). Es una denegación esperada, no un fallo:
 * reintentarla no cambia la respuesta y mostrarla como error tampoco aplica.
 * Cualquier otro 403 (`TENANT_ADMIN_REQUIRED`, `ACCOUNT_NOT_ACTIVE`…) no
 * entra aquí.
 */
export function isForbiddenTier(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.code === 'FORBIDDEN_TIER'
}

/**
 * El backend negó la operación por el estado de la organización (OT-0072):
 * suspendida, o en solo lectura y la operación escribe. Si llega, la UI tenía
 * un estado viejo de `/me` (p. ej. cambió con la sesión abierta).
 */
export function isTenantStateDenial(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    (error.code === 'TENANT_READ_ONLY' || error.code === 'TENANT_SUSPENDED')
  )
}

/**
 * Por qué el backend niega la plataforma a un platform admin (OT-0073 §4.1):
 * el token no acredita MFA (`amr`) o el usuario no es del tenant operador.
 * Reintentar no cambia la respuesta; la UI pide volver a entrar.
 */
export type PlatformAccessDenial = 'mfa' | 'operator'

export function platformAccessDenial(error: unknown): PlatformAccessDenial | null {
  if (!(error instanceof ApiError) || error.status !== 403) return null
  if (error.code === 'PLATFORM_MFA_REQUIRED') return 'mfa'
  if (error.code === 'PLATFORM_OPERATOR_REQUIRED') return 'operator'
  return null
}

/**
 * El backend no encontró el recurso. Por diseño (`_not_found` en
 * `app/core/permissions.py`) responde lo mismo si no existe, si es de otro
 * tenant o si el usuario no puede verlo, para no revelar su existencia. La UI
 * no debe distinguir esos casos: un 404 se presenta como "no encontrado".
 */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

/**
 * fetch con headers de auth + i18n ya añadidos. Devuelve el JSON parseado
 * o tira `ApiError` si el status no es 2xx. El consumer típico llama
 * `t(error.code, error.body?.details)` para renderizar copy localizada
 * desde su propio catálogo `errors.json` (Doc 19 §3.1).
 */
export async function apiFetch<T>(
  path: string,
  { accessToken, activeTenantId, method = 'GET', body }: ApiFetchOptions,
): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'Accept-Language': i18n.language || 'es',
  }
  if (activeTenantId) {
    headers['X-Active-Tenant'] = activeTenantId
  }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const init: RequestInit = { method, headers }
  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }
  const response = await fetch(`${API_BASE}${path}`, init)

  const text = await response.text()
  if (!response.ok) {
    throw new ApiError(response.status, text)
  }
  if (!text) {
    return undefined as T
  }
  return JSON.parse(text) as T
}

/**
 * Variante de `apiFetch` para `multipart/form-data` (uploads de archivos).
 * NO seteamos `Content-Type` manualmente — el browser lo añade con el
 * boundary correcto al detectar `FormData`. Usado por Sources upload
 * (Sprint A de B.6.d.10).
 */
export async function apiFetchForm<T>(
  path: string,
  {
    accessToken,
    activeTenantId,
    method = 'POST',
    formData,
  }: {
    accessToken: string
    activeTenantId?: string | undefined
    method?: 'POST' | 'PUT' | 'PATCH'
    formData: FormData
  },
): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'Accept-Language': i18n.language || 'es',
  }
  if (activeTenantId) {
    headers['X-Active-Tenant'] = activeTenantId
  }
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: formData,
  })
  const text = await response.text()
  if (!response.ok) {
    throw new ApiError(response.status, text)
  }
  if (!text) {
    return undefined as T
  }
  return JSON.parse(text) as T
}

/**
 * Descarga binaria. Devuelve `(blob, filename)` extraído del header
 * `Content-Disposition`. Usado por Sources download.
 */
export async function apiFetchBinary(
  path: string,
  { accessToken, activeTenantId }: ApiFetchOptions,
): Promise<{ blob: Blob; filename: string | null }> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'Accept-Language': i18n.language || 'es',
  }
  if (activeTenantId) {
    headers['X-Active-Tenant'] = activeTenantId
  }
  const response = await fetch(`${API_BASE}${path}`, { method: 'GET', headers })
  if (!response.ok) {
    const errorText = await response.text()
    throw new ApiError(response.status, errorText)
  }
  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/i.exec(disposition)
  const filename = match?.[1] ?? null
  return { blob, filename }
}

// ─── Server-Sent Events ─────────────────────────────────────────────────────
/** Un evento SSE del backend: `event:` → `type`, `data:` (JSON) → `payload`. */
export interface SseEvent {
  type: string
  payload: unknown
}

/** Se cortó la conexión antes del evento final del stream. */
export const STREAM_INTERRUPTED = 'STREAM_INTERRUPTED'

/**
 * Petición que responde `text/event-stream`: POST en los flujos de IA de
 * larga duración (OT-0040), GET en el stream de notificaciones. Yield de cada
 * evento conforme llega.
 *
 * Usa `fetch()` y no `EventSource` porque éste no manda headers: el token iría
 * en la URL y quedaría en los logs (OT-0084).
 * Un status no-2xx (validaciones antes de abrir el stream) se lanza como
 * `ApiError`; los fallos durante el stream llegan como evento `error`.
 * Si se cae la red o el stream termina sin uno de los eventos `terminal`, se
 * lanza `ApiError` `STREAM_INTERRUPTED` (mensaje traducido). Si el consumidor
 * deja de leer (break, `signal` abortado), la conexión se cierra y el backend
 * cancela el flujo; un abort se propaga tal cual.
 */
export async function* apiStream<E extends SseEvent>(
  path: string,
  {
    accessToken,
    method = 'POST',
    body,
    signal,
    terminal,
  }: {
    accessToken: string
    method?: 'GET' | 'POST'
    /** Cuerpo JSON del POST; un GET no lleva. */
    body?: unknown
    signal?: AbortSignal | undefined
    terminal: readonly E['type'][]
  },
): AsyncGenerator<E, void, void> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'Accept-Language': i18n.language || 'es',
    Accept: 'text/event-stream',
  }
  const init: RequestInit = { method, headers }
  if (method === 'POST') {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(body)
  }
  if (signal) {
    init.signal = signal
  }
  const response = await fetch(`${API_BASE}${path}`, init).catch(
    (err: unknown) => {
      throw signal?.aborted ? err : streamInterrupted()
    },
  )
  if (!response.ok) {
    throw new ApiError(response.status, await response.text())
  }
  if (!response.body) {
    throw streamInterrupted()
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  let ended = false
  try {
    while (true) {
      const { value, done } = await reader.read().catch((err: unknown) => {
        throw signal?.aborted ? err : streamInterrupted()
      })
      if (done) break
      buffer += value
      // SSE separa eventos con una línea en blanco.
      let sepIdx = buffer.indexOf('\n\n')
      while (sepIdx !== -1) {
        const evt = parseSseBlock<E>(buffer.slice(0, sepIdx))
        buffer = buffer.slice(sepIdx + 2)
        if (evt) {
          ended = terminal.includes(evt.type)
          yield evt
        }
        sepIdx = buffer.indexOf('\n\n')
      }
    }
    if (buffer.trim().length > 0) {
      const evt = parseSseBlock<E>(buffer)
      if (evt) {
        ended = terminal.includes(evt.type)
        yield evt
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined)
  }
  if (!ended) throw streamInterrupted()
}

/** La conexión se perdió antes del evento final del stream. */
function streamInterrupted(): ApiError {
  return ApiError.fromBody(0, {
    code: STREAM_INTERRUPTED,
    message: i18n.t('errors:STREAM_INTERRUPTED'),
  })
}

/** Un bloque SSE (`event:` + `data:`) al evento tipado. Sin `event:` (p. ej.
 * un comentario `:`) no es un evento. */
function parseSseBlock<E extends SseEvent>(block: string): E | null {
  let event: string | null = null
  const dataLines: string[] = []
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }
  if (event === null) return null
  const dataStr = dataLines.join('\n')
  let payload: unknown = {}
  if (dataStr) {
    try {
      payload = JSON.parse(dataStr)
    } catch {
      payload = { message: dataStr }
    }
  }
  return { type: event, payload } as E
}

/** Payload del evento `error` de un stream: `code` estable + `message`
 * traducido por el backend. */
export interface StreamErrorPayload {
  step?: string | null
  code?: string
  message: string
}

/**
 * Consume un stream cuyo resultado llega en un evento `done`: resuelve con
 * su payload, o lanza `ApiError` con el `code`/`message` del evento `error`
 * (mismo manejo que un error REST). `onEvent` recibe los eventos de
 * progreso (pasos, `waiting`).
 */
export async function apiStreamResult<T>(
  events: AsyncIterable<SseEvent>,
  onEvent?: (event: SseEvent) => void,
): Promise<T> {
  for await (const event of events) {
    if (event.type === 'done') return event.payload as T
    if (event.type === 'error') {
      const payload = event.payload as StreamErrorPayload
      throw ApiError.fromBody(0, {
        code: payload.code ?? 'UNKNOWN',
        message: payload.message,
      })
    }
    onEvent?.(event)
  }
  throw streamInterrupted()
}
